import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { FieldPath, FieldValues, useController } from 'react-hook-form';
import { AddressItem, AddressSelect } from '@/features/address/ui/address-select';
import { QueryApi } from '@/features/query/api';
import { Address } from '@/shared/model/Address';
import { FormFieldProps } from './types';

type Props<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = FormFieldProps<typeof AddressSelect, TFieldValues, TName>;

export const FormUserAddressSelect = <
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
    name,
    control,
    defaultValue,
    disabled,
    rules,
    shouldUnregister,
    onValueChange,
    onSuccess,
    ...props
}: Props<TFieldValues, TName>) => {
    const {
        field: { ref: fieldRef, onChange, value },
    } = useController({ name, control, defaultValue, disabled, rules, shouldUnregister });

    const customerQuery = useQuery(QueryApi.Customer.queries.Customer);
    const isLoggedIn = !!customerQuery.data;
    const addressesQuery = useQuery({
        ...QueryApi.Customer.queries.Addresses({ offset: 0, limit: 100 }),
        enabled: isLoggedIn,
    });
    const allAddresses = useMemo(
        () => (addressesQuery.data?.items ?? []) as AddressItem[],
        [addressesQuery.data?.items],
    );

    const handleChange = (id: string | null, address: Address | null) => {
        onChange(
            !id || !address
                ? null
                : {
                      id,
                      city: address.city,
                      country: address.country,
                      postCode: address.postCode,
                      street: address.street,
                      lat: address.lat!,
                      lon: address.lon!,
                  },
        );
    };

    const handleOnValueChange = (id: string | null, address: Address | null) => {
        handleChange(id, address);
        onValueChange?.(id, address);
    };

    const handleOnSuccess = (id: string | null, address: Address | null) => {
        handleChange(id, address);
        onValueChange?.(id, address);
        onSuccess?.(id, address);
    };

    return (
        <AddressSelect
            triggerProps={{ ref: fieldRef }}
            addresses={allAddresses}
            isLoading={addressesQuery.isLoading}
            value={value}
            onValueChange={handleOnValueChange}
            onSuccess={handleOnSuccess}
            {...props}
        />
    );
};
